import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

import { Arrow, Preloader, Icon, EmptyButton } from 'components';
import { NavLink } from 'react-router-dom';

import styles from './styles.pcss';

const cx = classNames.bind(styles);

const Container = ({ nameId, children }) => (
  <div
    className={cx('container')}
    id={nameId}
  >
    {children}
  </div>
);

Container.propTypes = {
  nameId: PropTypes.string,
  children: PropTypes.any,
};

class AnimationWrapper extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isHidden: true,
    };
  }

  componentDidMount() {
    this.animationTimeout = setTimeout(() => {
      this.setState({ isHidden: false });
    });

    if (this.props.scrollToBlock) {
      this.scrollToTimeout = setTimeout(() => {
        const element = document.getElementById(this.props.scrollToBlock);

        if (!element) return;

        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        }, 100);
      }, 1500);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.animationTimeout);
    clearTimeout(this.scrollToTimeout);
  }

  render() {
    const { isHidden } = this.state;
    const { className, children } = this.props;

    return (
      <div
        className={cx('animationWrapper', { isHidden }, className)}
      >
        {children}
      </div>
    );
  }
}

AnimationWrapper.propTypes = {
  className: PropTypes.any,
  children: PropTypes.any,
  scrollToBlock: PropTypes.any,
};

const LinkContent = ({ children }) => (
  <>
    <span className={cx('linkContent')}>
      {children}
    </span>
    <Arrow className={cx('linkArrow')} />
  </>
);

LinkContent.propTypes = {
  children: PropTypes.any,
};

const Link = ({
  children,
  href,
  to,
  onClick,
}) => {
  if (to) {
    return (
      <NavLink
        className={cx('link')}
        onClick={onClick || null}
        to={to}
      >
        <LinkContent>
          {children}
        </LinkContent>
      </NavLink>
    );
  }

  const ComponentName = href ? 'a' : EmptyButton;

  return (
    <ComponentName
      className={cx('link')}
      onClick={onClick || null}
      href={href}
    >
      <LinkContent>
        {children}
      </LinkContent>
    </ComponentName>
  );
};

Link.propTypes = {
  children: PropTypes.any,
  href: PropTypes.string,
  to: PropTypes.string,
  onClick: PropTypes.func,
};

const Block = ({
  children,
  className,
  title,
  subtitle,
  links,
  info,
  description,
  refreshAction,
  isLoading,
  isCenteredContent,
  scrollToBlock,
  nameId,
  ariaLabel,
  regionInfo,
}) => {
  if (!title && !children) {
    return null;
  }

  const blockLinks = links && links.filter((l) => l);

  return (
    <AnimationWrapper
      className={cx('block', className)}
      scrollToBlock={scrollToBlock && nameId}
    >
      <section
        className={cx('blockWrapper')}
        aria-label={ariaLabel}
        id={nameId}
      >
        {title && (
          <div className={cx('blockHeading')}>
            <h3 className={cx('blockHeadingTitle')}>
              {title}
            </h3>
            {info && (
              <div className={cx('blockHeadingInfo')}>
                {info}
              </div>
            )}
            {refreshAction && (
              <button
                className={cx('blockRefreshButton')}
                onClick={refreshAction}
              >
                Обновить
                <Icon name="reload" />
              </button>
            )}
            {subtitle && (
              <div className={cx('blockHeadingSubtitle')}>
                {subtitle}
              </div>
            )}
          </div>
        )}
        {regionInfo && (
          <div className={cx('blockRegionInfo')}>
            {regionInfo}
          </div>
        )}
        {description && (
          <div className={cx('blockDescription')}>
            {description}
          </div>
        )}
        {children && (
          <div className={cx('blockContent', { centeredContent: isCenteredContent })}>
            {!isLoading && children}
            {isLoading && (
              <Preloader className={cx('preloader')} size="80" />
            )}
          </div>
        )}
        {blockLinks && links.length > 0 && (
          <div className={cx('blockLinks')}>
            {links.map((link, index) => link && (
              <Link key={index} {...link} />
            ))}
          </div>
        )}
      </section>
    </AnimationWrapper>
  );
};

Block.propTypes = {
  children: PropTypes.any,
  className: PropTypes.any,
  title: PropTypes.any,
  subtitle: PropTypes.any,
  info: PropTypes.any,
  description: PropTypes.any,
  refreshAction: PropTypes.func,
  links: PropTypes.array,
  isLoading: PropTypes.bool,
  isCenteredContent: PropTypes.bool,
  scrollToBlock: PropTypes.bool,
  nameId: PropTypes.string,
  ariaLabel: PropTypes.string,
  regionInfo: PropTypes.any,
};

const ContentGroup = ({
  children,
  title,
  info,
  description,
  regionInfo,
}) => (
  <div className={cx('contentGroup')}>
    {title && (
      <div className={cx('blockHeading')}>
        <h3 className={cx('blockHeadingTitle')}>
          {title}
        </h3>
        {info && (
          <div className={cx('blockHeadingInfo')}>
            {info}
          </div>
        )}
      </div>
    )}
    {regionInfo && (
    <div className={cx('blockRegionInfo')}>
      {regionInfo}
    </div>
    )}
    {description && (
      <div className={cx('blockDescription')}>
        {description}
      </div>
    )}
    {children && (
      <div className={cx('blockContent')}>
        {children}
      </div>
    )}
  </div>
);

ContentGroup.propTypes = {
  children: PropTypes.any,
  title: PropTypes.any,
  info: PropTypes.any,
  description: PropTypes.any,
  regionInfo: PropTypes.any,
};

const Table = ({ className, children }) => (
  <table className={cx('table', className)}>
    <tbody>
      {children}
    </tbody>
  </table>
);

Table.propTypes = {
  className: PropTypes.any,
  children: PropTypes.any,
};

const TableRow = ({ className, children }) => (
  <tr className={cx('tableRow', className)}>
    {children}
  </tr>
);

TableRow.propTypes = {
  className: PropTypes.any,
  children: PropTypes.any,
};

const TableCell = ({ className, children, ...props }) => (
  <td {...props} className={cx('tableCell', className)}>
    {children}
  </td>
);

TableCell.propTypes = {
  className: PropTypes.any,
  children: PropTypes.any,
};

export default {
  Container,
  Block,
  AnimationWrapper,
  ContentGroup,
  Table,
  TableRow,
  TableCell,
};
